import { Badge, Button, cn } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDateLocal } from "@/shared/lib/date-time";
import { usePerformanceProjectMyTasksQuery } from "../api/queries";
import { assignedTaskPriorityBadgeClass } from "../lib/assignedTaskStatusStyles";
import {
  labelAssignedTaskAlertStatus,
  labelProjectTaskPriority,
} from "../lib/performanceLaoLabels";
import { performanceRoutes } from "../performanceRoutes";
import { AssignedTaskStatusSelect } from "../ui/AssignedTaskStatusSelect";

const PROJECT_STATUS_LABEL: Record<"active" | "completed" | "on_hold", string> =
  {
    active: "ດຳເນີນຢູ່",
    completed: "ສຳເລັດ",
    on_hold: "ຢຸດຊົ່ວຄາວ",
  };

export function PerformanceProjectMyTasksPage() {
  const navigate = useNavigate();
  const { projectId } = useParams({
    from: "/app/performance/projects/$projectId",
  });
  const { data, isLoading, isError } =
    usePerformanceProjectMyTasksQuery(projectId);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">
              {data?.project.name ?? "ວຽກໃນໂຄງການ"}
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ວຽກທີ່ມອບໝາຍໃຫ້ທ່ານໃນໂຄງການນີ້ເທົ່ານັ້ນ
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate({ to: performanceRoutes.tasks })}
          >
            <ArrowLeft className="size-4" />
            ກັບໄປລາຍການວຽກ
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <div className="h-20 animate-pulse rounded-xl border bg-muted/40" />
            <div className="h-20 animate-pulse rounded-xl border bg-muted/40" />
          </div>
        ) : isError ? (
          <p className="py-12 text-center text-muted-foreground">
            ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ
          </p>
        ) : data == null ? (
          <p className="py-12 text-center text-muted-foreground">
            ບໍ່ພົບໂຄງການນີ້ ຫຼື ທ່ານບໍ່ມີວຽກໃນໂຄງການນີ້
          </p>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="secondary">
                {PROJECT_STATUS_LABEL[data.project.status]}
              </Badge>
            </div>
            {data.tasks.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                ບໍ່ມີວຽກໃນລາຍການ
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-xl border">
                {data.tasks.map((t) => {
                  const u = t.alertStatus;
                  const pendingLike = t.status !== "done";
                  return (
                    <li
                      key={t.id}
                      className={cn(
                        "flex flex-col gap-2 px-4 py-4 sm:flex-row sm:items-center sm:justify-between",
                        t.alertStatus === "overdue" &&
                          "border-l-4 border-l-destructive bg-destructive/5",
                        t.alertStatus === "due_soon" &&
                          "border-l-4 border-l-amber-600 bg-amber-500/5 dark:border-l-amber-500",
                      )}
                    >
                      <div className="min-w-0">
                        <p className="font-medium">{t.title}</p>
                        <p className="text-muted-foreground text-xs">
                          {t.dueDate
                            ? `ກຳນົດ ${formatDateLocal(t.dueDate)}`
                            : "ບໍ່ມີວັນຄົບ"}
                          {t.progressPercent != null
                            ? ` · ຄວາມຄືບໜ້າ ${t.progressPercent}%`
                            : ""}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {pendingLike && u === "overdue" ? (
                          <Badge variant="destructive">
                            {labelAssignedTaskAlertStatus("overdue")}
                          </Badge>
                        ) : null}
                        {pendingLike && u === "due_soon" ? (
                          <Badge
                            variant="outline"
                            className="border-amber-600/60 text-amber-950 dark:border-amber-500/50 dark:text-amber-100"
                          >
                            {labelAssignedTaskAlertStatus("due_soon")}
                          </Badge>
                        ) : null}
                        {t.priority ? (
                          <Badge
                            variant="outline"
                            className={assignedTaskPriorityBadgeClass(
                              t.priority,
                              t.status,
                            )}
                          >
                            ຄວາມສຳຄັນ {labelProjectTaskPriority(t.priority)}
                          </Badge>
                        ) : null}
                        <AssignedTaskStatusSelect
                          variant="project"
                          id={t.id}
                          status={t.status}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </Main>
    </>
  );
}
